import { RuleActionType } from '@jupiterone/jupiter-types';
import { JSONInput, JupiterTheme } from '@jupiterone/jupiter-web-toolkit';
import {
  IconButton,
  InputAdornment,
  TextField,
  Tooltip,
} from '@material-ui/core';
import { createStyles, makeStyles } from '@material-ui/styles';
import React, { ChangeEvent, useEffect, useState } from 'react';
import HelpIcon from 'react-feather/dist/icons/help-circle';
import { RuleActionData } from 'jupiter-web-alerts/src/types';

const useStyles = makeStyles((theme: JupiterTheme) =>
  createStyles({
    full: {
      display: 'block',
      marginBottom: theme.spacing(1),
    },
    container: {
      marginBottom: theme.spacing(3),
    },
  })
);

export interface RuleActionConfigurationField {
  label: string;
  initialValue?: any;
  helpText?: string;
  type?: 'string' | 'number' | 'json';
}

interface Props {
  type: RuleActionType;
  initialActionData?: RuleActionData;
  fields: {
    [name: string]: RuleActionConfigurationField;
  };
  disabled?: boolean;
  onUpdate: (newAction: RuleActionData) => void;
}

interface SimpleRuleConfigForm {
  [name: string]: string | object;
}
export const useSimpleRuleConfig = ({
  type,
  fields,
  initialActionData,
  disabled,
  onUpdate,
}: Props) => {
  const [values, setValues] = useState<SimpleRuleConfigForm>(
    Object.keys(fields).reduce((acc: SimpleRuleConfigForm, k: any) => {
      const field = fields[k];
      if (initialActionData && initialActionData[k]) {
        acc[k] = initialActionData[k];
      } else {
        acc[k] = field.initialValue !== undefined ? field.initialValue : '';
      }
      return acc;
    }, {} as any)
  );

  useEffect(() => {
    if (!disabled) {
      onUpdate({ type, ...values } as any);
    }
  }, [values, disabled]);

  const handleChange = (e: ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setValues({
      ...(initialActionData || {}),
      ...values,
      [name]: value,
    });
  };
  return {
    values,
    handleChange,
  };
};
const SimpleRuleConfig = ({
  type,
  fields,
  initialActionData,
  disabled,
  onUpdate,
}: Props) => {
  const classes = useStyles();
  const { values, handleChange } = useSimpleRuleConfig({
    type,
    fields,
    initialActionData,
    disabled,
    onUpdate,
  });

  return (
    <div className={classes.container}>
      {Object.keys(fields).map((k: string) => {
        switch (fields[k]?.type) {
          case 'json':
            return (
              <JSONInput
                key={k}
                value={values[k] as object}
                onChange={handleChange}
                label={fields[k].label}
                disabled={disabled}
                name={k}
              />
            );
          default:
            return (
              <TextField
                key={k}
                className={classes.full}
                fullWidth={true}
                onChange={handleChange}
                name={k}
                value={values[k]}
                label={fields[k].label}
                disabled={disabled}
                InputProps={{
                  endAdornment: fields[k].helpText && (
                    <InputAdornment position="end">
                      <Tooltip title={fields[k].helpText || ''}>
                        <IconButton>
                          <HelpIcon size={20} />
                        </IconButton>
                      </Tooltip>
                    </InputAdornment>
                  ),
                }}
              />
            );
        }
      })}
    </div>
  );
};

export default SimpleRuleConfig;
